/**
 * Checklist State Management Hook
 * Manages checklist responses, completion status, and quantities per instance
 */

import { useCallback, useState } from "react";
import type { ChecklistResponseValue } from "@/types/quote.types";

export const useChecklistState = () => {
  // Instance-based state: instanceId -> checklistId -> responses
  const [instanceResponses, setInstanceResponses] = useState<
    Record<number, Record<number, Record<string, ChecklistResponseValue>>>
  >({});

  // Instance-based completed checklists: instanceId -> checklistId[]
  const [instanceCompletedChecklists, setInstanceCompletedChecklists] = useState<
    Record<number, number[]>
  >({});

  // Quantities: checklistId -> fieldName -> quantity
  const [quantities, setQuantities] = useState<Record<number, Record<string, number>>>({});

  /**
   * Get responses for a specific instance and checklist
   */
  const getInstanceResponses = useCallback(
    (instanceId: number, checklistId: number): Record<string, ChecklistResponseValue> => {
      return instanceResponses[instanceId]?.[checklistId] || {};
    },
    [instanceResponses]
  );

  /**
   * Set a response for a specific instance and checklist
   */
  const setInstanceResponse = useCallback(
    (instanceId: number, checklistId: number, fieldName: string, value: ChecklistResponseValue) => {
      setInstanceResponses((prev) => ({
        ...prev,
        [instanceId]: {
          ...prev[instanceId],
          [checklistId]: {
            ...prev[instanceId]?.[checklistId],
            [fieldName]: value,
          },
        },
      }));
    },
    []
  );

  /**
   * Update all responses for a checklist in an instance
   */
  const updateChecklistResponses = useCallback(
    (
      instanceId: number,
      checklistId: number,
      responses: Record<string, ChecklistResponseValue>
    ) => {
      setInstanceResponses((prev) => ({
        ...prev,
        [instanceId]: {
          ...prev[instanceId],
          [checklistId]: responses,
        },
      }));
    },
    []
  );

  /**
   * Mark a checklist as complete for an instance
   */
  const markChecklistComplete = useCallback((instanceId: number, checklistId: number) => {
    setInstanceCompletedChecklists((prev) => {
      const currentCompleted = prev[instanceId] || [];
      if (currentCompleted.includes(checklistId)) {
        return prev;
      }
      return {
        ...prev,
        [instanceId]: [...currentCompleted, checklistId],
      };
    });
  }, []);

  /**
   * Unmark a checklist as complete for an instance
   */
  const unmarkChecklistComplete = useCallback((instanceId: number, checklistId: number) => {
    setInstanceCompletedChecklists((prev) => ({
      ...prev,
      [instanceId]: (prev[instanceId] || []).filter((id) => id !== checklistId),
    }));
  }, []);

  /**
   * Get completed checklists for an instance
   */
  const getInstanceCompletedChecklists = useCallback(
    (instanceId: number): number[] => {
      return instanceCompletedChecklists[instanceId] || [];
    },
    [instanceCompletedChecklists]
  );

  /**
   * Check if a checklist is complete for an instance
   */
  const isChecklistComplete = useCallback(
    (instanceId: number, checklistId: number): boolean => {
      return (instanceCompletedChecklists[instanceId] || []).includes(checklistId);
    },
    [instanceCompletedChecklists]
  );

  /**
   * Update quantity for a component
   */
  const updateQuantity = useCallback((checklistId: number, fieldName: string, quantity: number) => {
    setQuantities((prev) => ({
      ...prev,
      [checklistId]: {
        ...prev[checklistId],
        [fieldName]: quantity,
      },
    }));
  }, []);

  /**
   * Get quantity for a component
   */
  const getQuantity = useCallback(
    (checklistId: number, fieldName: string): number | undefined => {
      return quantities[checklistId]?.[fieldName];
    },
    [quantities]
  );

  /**
   * Load existing state (for editing existing quotes)
   */
  const loadChecklistState = useCallback(
    (
      loadedResponses: Record<number, Record<number, Record<string, ChecklistResponseValue>>>,
      loadedCompleted: Record<number, number[]>,
      loadedQuantities: Record<number, Record<string, number>>
    ) => {
      setInstanceResponses(loadedResponses);
      setInstanceCompletedChecklists(loadedCompleted);
      setQuantities(loadedQuantities);
    },
    []
  );

  /**
   * Clear all responses for an instance (when deleted)
   */
  const clearInstanceData = useCallback((instanceId: number) => {
    setInstanceResponses((prev) => {
      const { [instanceId]: removed, ...rest } = prev;
      return rest;
    });
    setInstanceCompletedChecklists((prev) => {
      const { [instanceId]: removed, ...rest } = prev;
      return rest;
    });
  }, []);

  /**
   * Get all responses across all instances (for save/export)
   */
  const getAllResponses = useCallback(() => {
    return instanceResponses;
  }, [instanceResponses]);

  /**
   * Get all completed checklists across all instances
   */
  const getAllCompletedChecklists = useCallback(() => {
    return instanceCompletedChecklists;
  }, [instanceCompletedChecklists]);

  return {
    // Response management
    getInstanceResponses,
    setInstanceResponse,
    updateChecklistResponses,
    getAllResponses,

    // Completion status
    markChecklistComplete,
    unmarkChecklistComplete,
    getInstanceCompletedChecklists,
    isChecklistComplete,
    getAllCompletedChecklists,

    // Quantity management
    updateQuantity,
    getQuantity,
    quantities,

    // Bulk operations
    loadChecklistState,
    clearInstanceData,
  };
};
